import { Divider, IconName, Stack } from '@optimus-web/core';
import {
    EnumSelectField,
    LocalizedFormInputFields,
    LocalizedFormTextareaFields,
    NumberInputField,
} from '@mews-commander/form';
import { PricingClassification, ReservationPurpose } from '@mews-commander/data-access';
import { FormCheckboxField, FormInputField, FormSwitchField, lt, useFeatureFlag } from '@mews/framework';
import { TooltippedIcon } from '@mews-commander/ui';
import { FeatureFlag } from '@mews-commander/enums';
import { DetailsFieldName } from '../types';
import { BaseRateField } from './BaseRateField';
import { RateGroupField } from './RateGroupField';
import { ServiceField } from './ServiceField';
import { AccountingCategoryField } from './AccountingCategoryField';
import { BusinessSegmentField } from './BusinessSegmentField';
import { VisibilityField } from './VisibilityField';

interface Props {
    baseRateId?: string | null;
    isCreateForm?: boolean;
}

export const RateFormFields = ({ baseRateId, isCreateForm = false }: Props) => {
    const isNordicChoiceRateOptionsEnabled = useFeatureFlag(FeatureFlag.nordicChoiceRateOptionsEnabled);

    const renderBaseRateField = () => {
        if (isCreateForm) {
            return <BaseRateField />;
        }

        return baseRateId ? <BaseRateField disabled /> : null;
    };

    return (
        <>
            <LocalizedFormInputFields name={DetailsFieldName.Names} label={lt('Name')} required />
            <LocalizedFormInputFields name={DetailsFieldName.ShortNames} label={lt('ShortName')} />
            <LocalizedFormInputFields
                name={DetailsFieldName.ExternalNames}
                label={lt('ExternalName')}
                captionIconWithTooltip={
                    <TooltippedIcon size="small" value={IconName.Info} tooltip={lt('ExternalNameHint')} />
                }
            />
            <LocalizedFormTextareaFields name={DetailsFieldName.Descriptions} label={lt('Description')} />
            <ServiceField />
            {renderBaseRateField()}
            <Stack vertical spacing="100">
                <FormCheckboxField name={DetailsFieldName.IsEnabled}>{lt('Activated')}</FormCheckboxField>
                <FormCheckboxField name={DetailsFieldName.IsDefault} disabled>
                    {lt('Default')}
                </FormCheckboxField>
            </Stack>
            <VisibilityField />
            <RateGroupField />
            <AccountingCategoryField />
            <BusinessSegmentField />
            <EnumSelectField
                name={DetailsFieldName.DefaultReservationPurpose}
                enumClass={ReservationPurpose}
                enumPrefix="ReservationPurpose"
                label={lt('DefaultReservationPurpose')}
                omitHint
            />
            <EnumSelectField
                name={DetailsFieldName.PricingClassification}
                enumClass={PricingClassification}
                enumPrefix="PricingClassification"
                label={lt('PricingClassification')}
                omitHint
            />
            <FormInputField
                name={DetailsFieldName.RateKey}
                label={lt('RateKey')}
                captionIconWithTooltip={
                    <TooltippedIcon size="small" value={IconName.Info} tooltip={lt('RateKeyHelperText')} />
                }
            />
            <NumberInputField required name={DetailsFieldName.Ordering} label={lt('Ordering')} />

            {isNordicChoiceRateOptionsEnabled ? (
                <>
                    <FormSwitchField
                        label={lt('RateBulkHidePriceFromGuest')}
                        helper={lt('RateOptionsHidePriceFromGuestHint')}
                        name={DetailsFieldName.HidePriceFromGuest}
                    />
                    <Divider />
                    <FormSwitchField
                        label={lt('RateBulkBonusPointsEligible')}
                        helper={lt('RateOptionsIsBonusPointsEligibleHint')}
                        name={DetailsFieldName.IsBonusPointsEligible}
                    />
                </>
            ) : null}
        </>
    );
};
